// src/components/RelationsSection.jsx
import React from "react";
import {
  Info, CheckCircle2, XCircle, AlertTriangle,
  Users2, Building2, Link2, ShieldCheck, Hash
} from "lucide-react";
import { Sekcja, RowIcon, mono } from "./atoms";

/** Compact, KNF-like status pill */
const StatusPill = ({ tone = "success", children }) => {
  const tones = {
    success: { bd: "#22c55e", fg: "#065f46", bg: "#ecfdf5", Icon: ShieldCheck },
    danger:  { bd: "#ef4444", fg: "#7f1d1d", bg: "#fef2f2", Icon: XCircle },
    warn:    { bd: "#f59e0b", fg: "#7c2d12", bg: "#fffbeb", Icon: AlertTriangle },
    neutral: { bd: "#e5e7eb", fg: "#374151", bg: "#f9fafb", Icon: Info },
  };
  const t = tones[tone] || tones.success;
  const Icon = t.Icon;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 12px",
        borderRadius: 999,
        border: `1.5px solid ${t.bd}`,
        color: t.fg,
        background: t.bg,
        fontWeight: 700,
        fontSize: 13,
      }}
    >
      <Icon size={16} />
      {children}
    </span>
  );
};

const dashedCard = {
  borderRadius: 12,
  padding: 12,

};

function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("aktywn")) return "success";
  if (s.includes("zlikwid") || s.includes("bankrut")) return "danger";
  if (s.includes("zawies") || s.includes("restrukt")) return "warn";
  return "neutral";
}

function filenameFromUrl(u) {
  try {
    const { pathname } = new URL(u);
    return decodeURIComponent(pathname.split("/").pop() || "plik");
  } catch {
    return "plik";
  }
}

/** Props: data = result?.json from relations API */
export default function RelationsSection({ data }) {
  if (!data) return null;

  const company   = data.company;
  const relations = data.relations;
  const graph     = data.graph;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {/* Header block — uses the same atoms as other sections */}
      <Sekcja
        title="Powiązania organizacyjno-osobowe"
        icon={Building2}
        titleStyle={{ fontSize: 20, color: "#0b74e5", textDecorationThickness: "2px", textUnderlineOffset: 4 }}
      >
        <div className="sectionGrid">
          <div>
            <RowIcon icon={Building2} label="Nazwa">
              {company?.name || <em>—</em>}
            </RowIcon>
            <RowIcon icon={Hash} label="NIP">
              {mono(company?.taxNumber)}
            </RowIcon>
            <RowIcon icon={Hash} label="KRS">
              {mono(company?.krsNumber)}
            </RowIcon>
            <RowIcon icon={Hash} label="REGON">
              {mono(company?.economyNumber)}
            </RowIcon>
          </div>

          <div>
            <RowIcon icon={Building2} label="Adres (1)">
              {company?.address?.firstLine || <em>—</em>}
            </RowIcon>
            <RowIcon icon={Building2} label="Adres (2)">
              {company?.address?.secondLine || <em>—</em>}
            </RowIcon>
            <RowIcon icon={ShieldCheck} label="Listy sankcyjne">
              {company?.sanctionLists
                ? company.sanctionLists.isOnSanctionList
                  ? <StatusPill tone="danger">Na listach sankcyjnych</StatusPill>
                  : <StatusPill tone="success">Brak na listach sankcyjnych</StatusPill>
                : <StatusPill tone="neutral">Brak danych</StatusPill>}
            </RowIcon>
          </div>

          {/* Full row: graph actions only (no preview) */}
          {graph?.fileUrl && (
            <div className="fullRow">
              <RowIcon icon={Link2} label="Pełny graf powiązań">
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <a
                    href={graph.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={btn.primary}
                  >
                    Otwórz w nowej karcie
                  </a>
                  <a href={graph.fileUrl} download style={btn.success}>
                    Pobierz plik
                  </a>
                  <button type="button" onClick={() => navigator.clipboard.writeText(graph.fileUrl)} style={btn.neutral}>
                    Kopiuj link
                  </button>
                  <code style={filenameTag}>{filenameFromUrl(graph.fileUrl)}</code>
                </div>
              </RowIcon>
            </div>
          )}
        </div>
      </Sekcja>

      {/* Companies via companies */}
      <Sekcja
        title="Powiązane firmy (przez spółki)"
        icon={Building2}
        titleStyle={{ fontSize: 18, color: "#0b74e5", textDecorationThickness: "2px", textUnderlineOffset: 4 }}
      >
        {renderCompanies(relations?.companiesRelatedToRoot)}
      </Sekcja>

      {/* Companies via people */}
      <Sekcja
        title="Powiązane firmy (przez osoby)"
        icon={Users2}
        titleStyle={{ fontSize: 18, color: "#0b74e5", textDecorationThickness: "2px", textUnderlineOffset: 4 }}
      >
        {renderCompanies(relations?.companiesRelatedToRootThroughPerson)}
      </Sekcja>

      {/* People */}
      <Sekcja
        title="Powiązane osoby"
        icon={Users2}
        titleStyle={{ fontSize: 18, color: "#0b74e5", textDecorationThickness: "2px", textUnderlineOffset: 4 }}
      >
        {renderPeople(relations?.peopleRelatedToCompany)}
      </Sekcja>
    </div>
  );
}

/* — Renderers that keep typography consistent — */
function renderCompanies(arr) {
  if (!arr || arr.length === 0) {
    return <em style={{ color: "#6b7280", fontStyle: "italic" }}>Brak danych</em>;
  }
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {arr.map((c, i) => (
        <div key={i} style={dashedCard}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <Building2 size={16} />
            <div style={{ fontWeight: 700 }}>{c.name || "—"}</div>
          </div>

          <div className="sectionGrid">
            <div>
              <RowIcon icon={Info} label="Forma prawna">{c.legalForm || <em>—</em>}</RowIcon>
              <RowIcon icon={Info} label="Status">
                {c.status
                  ? <StatusPill tone={statusTone(c.status)}>{c.status}</StatusPill>
                  : <em>—</em>}
              </RowIcon>
              <RowIcon icon={Info} label="Stopnie powiązań">
                {(c.relationLevels || []).join(", ") || <em>—</em>}
              </RowIcon>
            </div>
            <div>
              <RowIcon icon={Hash} label="KRS">{mono(c.krsNumber)}</RowIcon>
              <RowIcon icon={Hash} label="NIP">{mono(c.taxNumber)}</RowIcon>
              <RowIcon icon={Hash} label="REGON">{mono(c.economyNumber)}</RowIcon>
            </div>
            {c.sanctionLists && (
              <div className="fullRow">
                <RowIcon icon={ShieldCheck} label="Listy sankcyjne">
                  {c.sanctionLists.isOnSanctionList
                    ? <StatusPill tone="danger">Na listach sankcyjnych</StatusPill>
                    : <StatusPill tone="success">Brak na listach sankcyjnych</StatusPill>}
                  {(c.sanctionLists.sanctionListNames || []).length > 0 && (
                    <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                      {c.sanctionLists.sanctionListNames.map((n, idx) => <li key={idx}>{n}</li>)}
                    </ul>
                  )}
                </RowIcon>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function renderPeople(arr) {
  if (!arr || arr.length === 0) {
    return <em style={{ color: "#6b7280", fontStyle: "italic" }}>Brak danych</em>;
  }
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {arr.map((p, i) => (
        <div key={i} style={dashedCard}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <Users2 size={16} />
            <div style={{ fontWeight: 700 }}>{p.name || "—"}</div>
          </div>

          <div className="sectionGrid">
            <div>
              <RowIcon icon={Info} label="Stopnie powiązań">
                {(p.relationLevels || []).join(", ") || <em>—</em>}
              </RowIcon>
            </div>
            <div className="fullRow">
              {(p.relations || []).length > 0 ? (
                <RowIcon icon={Info} label="Relacje">
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {p.relations.map((r, idx) => (
                      <li key={idx} style={{ padding: "3px 0" }}>
                        {r.type || "—"} · {r.createdOn || "—"} · {r.companyName || "—"} (NIP: {r.companyTaxNumber || "—"})
                      </li>
                    ))}
                  </ul>
                </RowIcon>
              ) : (
                <RowIcon icon={Info} label="Relacje"><em>Brak danych</em></RowIcon>
              )}
            </div>
            {p.sanctionLists && (
              <div className="fullRow">
                <RowIcon icon={ShieldCheck} label="Listy sankcyjne">
                  {p.sanctionLists.isOnSanctionList
                    ? <StatusPill tone="danger">Na listach sankcyjnych</StatusPill>
                    : <StatusPill tone="success">Brak na listach sankcyjnych</StatusPill>}
                </RowIcon>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* — Small UI bits used above — */
const btnBase = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid transparent",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  textDecoration: "none",
};
const btn = {
  primary: { ...btnBase, background: "#0b74e5", color: "#fff" },
  success: { ...btnBase, background: "#10b981", color: "#fff" },
  neutral: { ...btnBase, background: "#f3f4f6", color: "#111827", border: "1px solid #e5e7eb" },
};
const filenameTag = {
  background: "#f3f4f6",
  border: "1px solid #e5e7eb",
  padding: "3px 8px",
  borderRadius: 8,
  fontSize: 12,
};
